import React, { useCallback, useEffect, useState } from 'react';
import { useToast } from '../sectepe-ui/index';
import mailboxService, { Mailbox, Kennzahlen, Snapshot } from '../services/mailboxService';
import { PageHeader, EmptyState, LoadingState, StatusBadge, useConfirm } from '../components/ui';
import { Icon } from '../components/icons';
import { SalesProcessBar } from '../vertrieb/SalesProcessBar';
import { KpiRow } from '../vertrieb/KpiRow';
import { fehlerText } from '../vertrieb/format';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';
import { rollenAufzaehlung, rollenMitRecht } from '../verwaltung/rollen';

/**
 * Versandpostfaecher und ihr Warm-up.
 *
 * Die Seite zeigt bewusst zuerst die Auffaelligkeiten und erst danach die
 * Zahlen. Ein Warm-up, das stillsteht, sieht in Lemlist aus wie eines, das
 * laeuft -- der Unterschied steht in `lastWarmAt`, und den sieht niemand
 * freiwillig nach.
 */

const zeit = (wert?: string | null) => {
  if (!wert) return '—';
  const d = new Date(wert);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
};

const vorStunden = (wert?: string | null): number | null => {
  if (!wert) return null;
  const d = new Date(wert);
  return Number.isNaN(d.getTime()) ? null : (Date.now() - d.getTime()) / 36e5;
};

const herKurz = (wert?: string | null) => {
  const h = vorStunden(wert);
  if (h === null) return '—';
  if (h < 1) return 'gerade eben';
  if (h < 48) return `vor ${Math.round(h)} h`;
  return `vor ${Math.round(h / 24)} Tagen`;
};

const scoreKlasse = (score?: number | null) => {
  if (score === null || score === undefined) return 'score-unbekannt';
  if (score >= 90) return 'score-gut';
  if (score >= 70) return 'score-mittel';
  return 'score-schlecht';
};

/** Kleine Verlaufskurve ohne Bibliothek -- 14 Punkte brauchen kein Chart. */
const Kurve: React.FC<{ punkte: Snapshot[] }> = ({ punkte }) => {
  const werte = [...punkte].reverse().map((p) => p.score).filter((s): s is number => s !== null && s !== undefined);
  if (werte.length < 2) return <span className="kurve-leer">zu wenig Verlauf</span>;

  const min = Math.min(...werte, 0);
  const max = Math.max(...werte, 100);
  const spanne = max - min || 1;
  const breite = 120;
  const hoehe = 28;
  const schritt = breite / (werte.length - 1);
  const pfad = werte
    .map((w, i) => `${i === 0 ? 'M' : 'L'} ${(i * schritt).toFixed(1)} ${(hoehe - ((w - min) / spanne) * hoehe).toFixed(1)}`)
    .join(' ');

  return (
    <svg className="kurve" width={breite} height={hoehe} role="img" aria-label="Verlauf der Zustellbarkeit">
      <path d={pfad} fill="none" strokeWidth="2" />
    </svg>
  );
};

const MailboxesPage: React.FC = () => {
  const { push } = useToast();
  const { confirm } = useConfirm();
  // Welle 2: POST /mailboxes/sync und Warm-up verlangen versand.ausfuehren;
  // Mitarbeit und Marketing sehen die Postfaecher nur lesend.
  const { darf } = useBerechtigung();
  const darfAusfuehren = darf('versand.ausfuehren');
  const [postfaecher, setPostfaecher] = useState<Mailbox[]>([]);
  const [kennzahlen, setKennzahlen] = useState<Kennzahlen | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [laeuftAbgleich, setLaeuftAbgleich] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [offen, setOffen] = useState<string | null>(null);

  const laden = useCallback(async () => {
    try {
      const d = await mailboxService.list();
      setPostfaecher(d?.postfaecher ?? []);
      setKennzahlen(d?.kennzahlen ?? null);
      setFehler(null);
    } catch (e) {
      setFehler(fehlerText(e, 'Laden fehlgeschlagen'));
    } finally {
      setLaedt(false);
    }
  }, []);

  useEffect(() => { laden(); }, [laden]);

  const abgleichen = async () => {
    setLaeuftAbgleich(true);
    setFehler(null);
    try {
      const b = await mailboxService.sync();
      const teile = [`${b.gefunden} Postfächer`, `${b.neu} neu`, `${b.aktualisiert} aktualisiert`];
      if (b.ohneWarmup) teile.push(`${b.ohneWarmup} ohne Warm-up-Daten`);
      if (b.fehler.length) teile.push(`${b.fehler.length} Fehler`);
      push({ message: teile.join(', '), severity: b.fehler.length ? 'warning' : 'success', duration: 8000 });
      await laden();
    } catch (e) {
      setFehler(fehlerText(e, 'Abgleich fehlgeschlagen'));
    } finally {
      setLaeuftAbgleich(false);
    }
  };

  const warmupStarten = async (box: Mailbox) => {
    const sicher = await confirm({
      title: `Warm-up für ${box.email} starten?`,
      message: 'Das löst Versand aus: Lemlist verschickt ab sofort täglich Warm-up-Mails über dieses Postfach.',
      confirmLabel: 'Warm-up starten',
    });
    if (!sicher) return;
    try {
      await mailboxService.startWarmup(box.id);
      push({ message: `Warm-up für ${box.email} gestartet.`, severity: 'success' });
      await laden();
    } catch (e) {
      setFehler(fehlerText(e, 'Warm-up konnte nicht gestartet werden'));
    }
  };

  const kritisch = postfaecher.filter((p) => p.warnungen.some((w) => w.stufe === 'kritisch'));

  return (
    <div className="page-container">
      <SalesProcessBar label="Postfächer" />
      <PageHeader
        title="Postfächer"
        subtitle="Warm-up und Zustellbarkeit je Postfach. Ein Warm-up, das stillsteht, sieht in Lemlist aus wie eines, das läuft – hier nicht."
        actions={darfAusfuehren ? (
          <button type="button" className="btn btn-primary" onClick={abgleichen} disabled={laeuftAbgleich}>
            <Icon name="refresh" size={14} /> {laeuftAbgleich ? 'Gleicht ab…' : 'Stand abgleichen'}
          </button>
        ) : undefined}
      />

      {!darfAusfuehren && (
        <p className="inbox-item-hinweis" data-testid="rechte-hinweis">
          Abgleichen und Warm-up starten sind {rollenAufzaehlung(rollenMitRecht('versand.ausfuehren'))} vorbehalten.
        </p>
      )}

      {fehler && <div className="alert alert-error">{fehler}</div>}

      {kennzahlen && (
        <KpiRow
          aria-label="Postfach-Kennzahlen"
          items={[
            { label: 'Postfächer', value: kennzahlen.postfaecher, hint: `zuletzt geprüft ${herKurz(kennzahlen.zuletztGeprueft)}` },
            { label: 'Warm-up aktiv', value: kennzahlen.warmupAktiv, tone: 'good' },
            { label: 'mit Problem', value: kennzahlen.kritisch, tone: kennzahlen.kritisch ? 'bad' : 'neutral' },
            { label: 'Zustellbarkeit ⌀', value: kennzahlen.scoreSchnitt ?? '—' },
            { label: 'Warm-up/Tag', value: kennzahlen.kapazitaet },
          ]}
        />
      )}

      {kritisch.length > 0 && (
        <div className="alert alert-error warnbox">
          <strong>{kritisch.length} Postfach/Postfächer brauchen Aufmerksamkeit:</strong>
          <ul>
            {kritisch.map((p) => (
              <li key={p.id}>
                <strong>{p.email}</strong> — {p.warnungen.filter((w) => w.stufe === 'kritisch').map((w) => w.text).join(' ')}
              </li>
            ))}
          </ul>
        </div>
      )}

      {laedt && !postfaecher.length && <LoadingState rows={3} label="Postfächer werden geladen" />}
      {!laedt && !postfaecher.length && (
        <EmptyState
          icon="mailbox"
          title="Noch keine Postfächer bekannt"
          description={darfAusfuehren
            ? '„Stand abgleichen“ holt sie aus Lemlist. Lemlist listet Postfächer nicht direkt – gefunden werden sie über die Kampagnen. Ein Postfach ohne Kampagne taucht erst auf, wenn es einer zugeordnet ist.'
            : 'Postfächer erscheinen hier, sobald jemand mit Versandrecht den Stand aus Lemlist abgleicht.'}
          action={darfAusfuehren ? { label: 'Stand abgleichen', onClick: abgleichen } : undefined}
          secondaryAction={{ label: 'Zu den Leads', to: '/lead-sync' }}
        />
      )}

      <div className="postfach-liste">
        {postfaecher.map((p) => (
          <div key={p.id} className={`postfach ${p.warnungen.some((w) => w.stufe === 'kritisch') ? 'postfach-kritisch' : ''}`}>
            <div className="postfach-kopf">
              <div>
                <strong>{p.email}</strong>
                {p.provider && <StatusBadge tone="neutral">{p.provider}</StatusBadge>}
                {p.warmupActive
                  ? <StatusBadge tone="info">Warm-up läuft</StatusBadge>
                  : <StatusBadge tone="warning">Warm-up aus</StatusBadge>}
              </div>
              <div className={`postfach-score ${scoreKlasse(p.score)}`}>
                {p.score ?? '—'}
                <small>Zustellbarkeit</small>
              </div>
            </div>

            <div className="postfach-zahlen">
              <span>letzte Warm-up-Mail <strong>{herKurz(p.lastWarmAt)}</strong></span>
              <span>Posteingang <strong>{p.inboxPercent !== null && p.inboxPercent !== undefined ? `${p.inboxPercent} %` : '—'}</strong></span>
              <span>gesendet <strong>{p.totalSent ?? '—'}</strong></span>
              <span>max/Tag <strong>{p.dailyMax ?? '—'}</strong></span>
              <span>DNS <strong>{p.dnsScore ?? '—'}</strong></span>
              <span className="postfach-kurve">
                <Kurve punkte={p.snapshots || []} />
                {p.verlauf.richtung !== 'unbekannt' && (
                  <small className={`verlauf-${p.verlauf.richtung}`}>
                    {p.verlauf.richtung}{p.verlauf.differenz ? ` (${p.verlauf.differenz > 0 ? '+' : ''}${p.verlauf.differenz})` : ''}
                  </small>
                )}
              </span>
            </div>

            {p.warnungen.length > 0 && (
              <ul className="postfach-warnungen">
                {p.warnungen.map((w, i) => (
                  <li key={i} className={`warnung-${w.stufe}`}>{w.text}</li>
                ))}
              </ul>
            )}

            <div className="postfach-aktionen">
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => setOffen(offen === p.id ? null : p.id)} aria-expanded={offen === p.id}>
                {offen === p.id ? 'weniger' : 'Details'}
              </button>
              {!p.warmupActive && darfAusfuehren && (
                <button type="button" className="btn btn-sm btn-primary" onClick={() => warmupStarten(p)}>
                  Warm-up starten
                </button>
              )}
            </div>

            {offen === p.id && (
              <div className="postfach-detail">
                <div className="akte-felder">
                  <label>Lemlist-Kennung<input readOnly value={p.lemlistId} /></label>
                  <label>Warm-up seit<input readOnly value={zeit(p.warmupStartedAt)} /></label>
                  <label>nächste Mail<input readOnly value={zeit(p.nextWarmAt)} /></label>
                  <label>Steigerung/Tag<input readOnly value={String(p.rampup ?? '—')} /></label>
                  <label>Postfachalter (Tage)<input readOnly value={String(p.mailboxAge ?? '—')} /></label>
                  <label>
                    letzter Bounce
                    <input readOnly value={`${zeit(p.lastBouncedAt)}${p.lastBounceReason ? ` (${p.lastBounceReason})` : ''}`} />
                  </label>
                </div>
                <p className="inbox-item-hinweis">
                  MX {p.mxOk === false ? '✗' : '✓'} · SPF {p.spfOk === false ? '✗' : '✓'} ·
                  DMARC {p.dmarcOk === false ? '✗' : '✓'} ·
                  Blacklist-Wert {p.blacklistScore ?? '—'}
                  {(p.blacklistedIn || []).length > 0 && ` · gelistet bei: ${(p.blacklistedIn || []).join(', ')}`}
                </p>
                {(p.tips || []).length > 0 && (
                  <>
                    <h4>Hinweise von Lemlist</h4>
                    <ul className="notiz-liste">
                      {(p.tips || []).map((t, i) => (
                        <li key={i}>
                          {t.title && <strong>{t.title}</strong>}
                          {t.title && t.message ? ' — ' : ''}
                          {t.message}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MailboxesPage;
